/**
 * A screenshot for the intake (POST /api/intake/analyze, `images`): the
 * backend takes JPEG / PNG / WebP data URLs of about 2 MB at most, so a
 * phone screenshot (3-6 MB PNG) is redrawn as a JPEG no wider than
 * 1600 px before it leaves the browser. Same decode path as the
 * listings camera (MagicSearchBar.prepareUpload); this one returns the
 * data URL the intake wants instead of a Blob.
 */
import { IMAGE_TYPES } from "@/lib/photo-pick";

/** Refuse the obviously wrong file before decoding it. */
export const INTAKE_IMAGE_MAX_BYTES = 20 * 1024 * 1024;
/** The intake's own cap on one data URL (routes/intake.py). */
export const INTAKE_DATA_URL_MAX = 2_800_000;
const MAX_SIDE = 1600;
/** Below this a JPEG / WebP is sent as it is; a PNG is always redrawn
 *  (screenshots are PNGs and large). */
const KEEP_BELOW_BYTES = 600 * 1024;

export class IntakeImageError extends Error {
  code: "unsupported" | "too_big" | "unreadable";
  constructor(code: "unsupported" | "too_big" | "unreadable") {
    super(code);
    this.code = code;
  }
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new IntakeImageError("unreadable"));
    r.readAsDataURL(blob);
  });
}

async function redraw(file: Blob, quality: number, maxSide: number): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  } finally {
    bitmap.close();
  }
}

/** The file as a data URL the intake accepts, or an IntakeImageError. */
export async function prepareIntakeImage(file: File): Promise<string> {
  if (!IMAGE_TYPES.includes(file.type)) throw new IntakeImageError("unsupported");
  if (file.size > INTAKE_IMAGE_MAX_BYTES) throw new IntakeImageError("too_big");
  if (file.type !== "image/png" && file.size <= KEEP_BELOW_BYTES) return readAsDataUrl(file);
  let out: Blob | null = null;
  try {
    out = await redraw(file, 0.85, MAX_SIDE);
    if (out && out.size * 1.37 > INTAKE_DATA_URL_MAX) out = await redraw(file, 0.7, 1280);
  } catch {
    out = null;
  }
  if (!out) {
    // Could not decode here: send the original when it is small enough.
    if (file.size * 1.37 > INTAKE_DATA_URL_MAX) throw new IntakeImageError("too_big");
    return readAsDataUrl(file);
  }
  const url = await readAsDataUrl(out);
  if (url.length > INTAKE_DATA_URL_MAX) throw new IntakeImageError("too_big");
  return url;
}
